import React from 'react';
import { connect } from 'react-redux';
import { ReduxState } from '../../reducers';
import { Box } from '@mui/material';
import { ClipLoader } from 'react-spinners';
import Text from '../common/Text';
import { SelectOption } from '../../common/types';
import { DueDiligenceChartDTO, DEFAULT_NEW_CHART } from '../../actions/types/dueDiligence';
import { createDueDiligenceChart, deleteDueDiligenceChart, getDueDiligenceCharts, selectDueDiligenceChart, processDueDiligenceChart, clearChartOptions, saveSelectedDueDiligenceChartUpdates } from '../../actions/dueDiligence';
import Button from '../common/Button';
import { Select } from '../common/styled/Select';
import SaveAsModal from '../CustomCMA/SaveAsModal';
import ConfirmDeleteSetModal from '../CustomCMA/ConfirmDeleteSetModal';
import Tabs from './Tabs';
import ChartSection from './ChartSection';

const CREATE_NEW_CHART_OPTION: SelectOption<DueDiligenceChartDTO> = { value: 0, label: 'Create New Chart', item: {...DEFAULT_NEW_CHART} } 

interface ReduxStateProps {
    loading: boolean;
    items: DueDiligenceChartDTO[]
    selectedChartItem: DueDiligenceChartDTO
}

interface ReduxActionProps {
    getDueDiligenceCharts: typeof getDueDiligenceCharts;
    deleteDueDiligenceChart: typeof deleteDueDiligenceChart;
    selectDueDiligenceChart: typeof selectDueDiligenceChart;
    processDueDiligenceChart: typeof processDueDiligenceChart;
    clearChartOptions: typeof clearChartOptions;
    saveSelectedDueDiligenceChartUpdates: typeof saveSelectedDueDiligenceChartUpdates;
    createDueDiligenceChart: (payload: Partial<DueDiligenceChartDTO>) => Promise<DueDiligenceChartDTO | null>;
}


interface ComponentState {
    items: SelectOption<DueDiligenceChartDTO>[]
    selectedChartItemOption: SelectOption<DueDiligenceChartDTO> | null
    showSaveAsModal: boolean
    showConfirmDeleteModal: boolean
}

type ComponentProps = ReduxActionProps & ReduxStateProps

class DueDiligenceCharts extends React.Component<ComponentProps, ComponentState> {
    state: ComponentState = {
        items: [CREATE_NEW_CHART_OPTION],
        showSaveAsModal: false,
        showConfirmDeleteModal: false,
        selectedChartItemOption: {...CREATE_NEW_CHART_OPTION},
    }

    componentDidMount(): void {
        this.props.getDueDiligenceCharts()
        
    }

    componentDidUpdate(prevProps: Readonly<ComponentProps>): void {
        if (prevProps.items !== this.props.items) {
            const items = this.props.items.map(item => ({
                value: item.id,
                label: item.name,
                item,
            }))
            this.setState({ items: [CREATE_NEW_CHART_OPTION, ...items] })
        }

        if (prevProps.selectedChartItem !== this.props.selectedChartItem) {
            if (this.props.selectedChartItem.id) {
                this.setState({ selectedChartItemOption: { value: this.props.selectedChartItem.id, label: this.props.selectedChartItem.name, item: this.props.selectedChartItem } })
            } else {
                this.setState({ selectedChartItemOption: {...CREATE_NEW_CHART_OPTION} })
            }
        }
    }

    onChartSelected = async (selectedChartItem: SelectOption<DueDiligenceChartDTO> | null) => {
        if (selectedChartItem?.item) {
            await this.props.selectDueDiligenceChart(selectedChartItem?.item)
            if (selectedChartItem.item.id) {
                this.props.processDueDiligenceChart()
            } else {
                this.props.clearChartOptions()
            }
        }
    }

    onSaveAs = () => {
        this.setState({ showSaveAsModal: true })
    }

    onCloseModals = () => {
        this.setState({ showSaveAsModal: false, showConfirmDeleteModal: false })
    }

    onSaveAsConfirmed = async (name: string) => {
        this.setState({ showSaveAsModal: false })
        const { selectedChartItem } = this.props;
        this.props.createDueDiligenceChart({ ...selectedChartItem, name });
    }

    onSave = () => {
        this.props.saveSelectedDueDiligenceChartUpdates()
    }

    onDelete = () => {
        this.setState({ showConfirmDeleteModal: true })
    }

    onConfirmDelete = () => {
        const {selectedChartItem} = this.props;
        if (!selectedChartItem.id) {
            return;
        }

        this.props.deleteDueDiligenceChart(selectedChartItem.id)
        this.setState({ showConfirmDeleteModal: false })
    }
        
    
    render() {
        const {loading, selectedChartItem} = this.props;
        const {items, showSaveAsModal, showConfirmDeleteModal, selectedChartItemOption} = this.state;
        const canModify = selectedChartItem.id > 0 && !selectedChartItem.is_system

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '50rem' }}>
                {loading && 
                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, alignItems: 'center', marginTop: '2rem' }}>
                        <Text>Loading...</Text>
                        <ClipLoader loading={true} size={14} />
                    </Box>
                }
                <SaveAsModal
                    visible={showSaveAsModal}
                    name={selectedChartItem.name ?? ''}
                    onSave={this.onSaveAsConfirmed}
                    onCancel={this.onCloseModals}
                />
                <ConfirmDeleteSetModal
                    visible={showConfirmDeleteModal}
                    item={selectedChartItem}
                    onConfirm={this.onConfirmDelete}
                    onCancel={this.onCloseModals}
                />
                <Box sx={{ display: 'flex', marginBottom: '1.5rem', alignItems: { xs: 'unset', xl: 'center' }, flexDirection: { xs: 'column', xl: 'row' } }}>
                    <Select sx={{ width: '35rem', marginRight: '1rem' }} options={items} value={selectedChartItemOption} onChange={this.onChartSelected} />

                    <Box sx={{ marginTop: { xs: '1rem', xl: 'unset' }, display: 'flex', alignItems: 'center' }}>
                        {canModify && <Button autoFit sx={{ height: '4.5rem' }} title={'Save'} onClick={this.onSave} />}
                        <Button autoFit sx={{ height: '4.5rem' }} title={'Save As'} onClick={this.onSaveAs} />
                        {canModify && <Button autoFit sx={{ height: '4.5rem' }} title={'Delete'} onClick={this.onDelete} />}
                    </Box>
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, height: '100%' }}>
                    <Tabs />
                    <ChartSection />
                </Box>
            </Box>
        )
    }
}


const mapStateToProps = ({ dueDiligence }: ReduxState): ReduxStateProps => {
    const { loadingCharts: loading, charts: items, selectedChartItem } = dueDiligence;
    return {
        items,
        loading,
        selectedChartItem,
    }
}

export default connect(mapStateToProps, { getDueDiligenceCharts, deleteDueDiligenceChart, createDueDiligenceChart, selectDueDiligenceChart, processDueDiligenceChart, clearChartOptions, saveSelectedDueDiligenceChartUpdates })(DueDiligenceCharts as any)